import React from 'react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ProjectChatData from './ProjectChatData';
import ProjectChatSources from './ProjectChatSources';

export default function ProjectChatAnalysis({ job, connected, onOpenEvent, onRequestCorrection, locked }) {
  const { t } = useTranslation();
  const answer = job?.analysis_result;
  if (job?.target !== 'analysis' || !answer) return null;
  const comparison = answer.comparison;
  const fileComparison = answer.file_comparison;
  const renderedComparison = ['rendered_pages', 'nonblank_rendered_pages'].includes(fileComparison?.basis);
  const ids = [...new Set((answer.lineage || []).flatMap(row => row.references || []).map(item => item.event_id))];
  return <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack spacing={1.5}>
      <Typography variant="subtitle1">{job.title || comparison?.title || t('project_chat.analysis.title')}</Typography>
      <Typography variant="body2">{t(job.result_disputed ? 'project_chat.codes.result_disputed'
        : answer.sources_changed ? 'project_chat.readable.older_result'
          : comparison ? `project_chat.readable.comparison.${comparison.status}`
            : fileComparison ? `project_chat.analysis.file_comparison.${renderedComparison ? 'rendered_' : ''}${fileComparison.status}`
              : answer.coverage ? `project_chat.analysis.coverage.${answer.coverage.status}`
              : 'project_chat.analysis.scope', { count: answer.events_checked })}</Typography>
      {fileComparison?.basis === 'nonblank_rendered_pages' && <Typography variant="body2">{t('project_chat.analysis.file_comparison.white_pages')}</Typography>}
      {comparison && <Typography variant="body2">{t('project_chat.readable.compared', {
        count: answer.events_checked, matched: comparison.matched, different: comparison.different,
        missing: comparison.missing, extra: comparison.extra })}</Typography>}
      {answer.event_results ? answer.event_results.map(result => <Stack key={result.event_id} spacing={0.5}>
        <Typography variant="subtitle2">{answer.source_names?.[result.event_id] || t('project_chat.event', { id: result.event_id })}</Typography>
        <Typography variant="body2">{t(`project_chat.readable.comparison.${result.comparison.status}`)}</Typography>
        <ProjectChatData value={result.rows} fields={job.analysis_plan?.fields?.map(field => ({ key: field.name, display_label: field.display_label }))} />
      </Stack>) : <ProjectChatData value={answer.rows} fields={job.analysis_plan?.fields?.map(field => ({ key: field.name, display_label: field.display_label }))} />}
      {comparison?.differences?.length > 0 && <>
        <Typography variant="subtitle2">{t('project_chat.readable.differences')}</Typography>
        <ProjectChatData value={comparison.differences} fields={[
          { key: 'saved', display_label: t('project_chat.readable.saved') },
          { key: 'checked', display_label: t('project_chat.readable.checked') },
          { key: 'field', display_label: t('project_chat.readable.item') },
          { key: 'record', display_label: t('project_chat.readable.record') }]} />
      </>}
      {answer.findings?.length > 0 && <>
        <Typography variant="subtitle2">{t('project_chat.readable.source_findings')}</Typography>
        {answer.findings.map((finding, index) => <Stack key={index} spacing={0.5}>
          <Typography variant="caption">{t(finding.page ? 'project_chat.readable.source_page' : 'project_chat.readable.source_named', {
            source: answer.source_names?.[finding.event_id] || t('project_chat.event', { id: finding.event_id }), page: finding.page })}</Typography>
          <Typography variant="body2">{finding.detail}{finding.number != null ? ` (${finding.number})` : ''}</Typography>
        </Stack>)}
      </>}
      {comparison?.status === 'differences' && !answer.sources_changed && !job.result_disputed && onRequestCorrection &&
        <Button variant="outlined" disabled={!connected || locked} onClick={() => onRequestCorrection(t('project_chat.outcome.correction_request', {
          title: comparison.title || job.title || '' }))}>{t('project_chat.outcome.correction')}</Button>}
      <Typography variant="caption">{t('project_chat.readable.check_scope', { count: answer.events_checked })}</Typography>
      <ProjectChatSources sources={ids.map(id => ({ event_id: id, title: answer.source_names?.[id] }))}
        connected={connected} onOpenEvent={onOpenEvent} />
    </Stack>
  </Paper>;
}
